import { bind } from "@react-rxjs/core"
import { t } from "i18next"
import { catchError, defer, distinctUntilChanged, map, of, tap } from "rxjs"

import {
  ERROR_MARKET_STATUS_RESULT,
  SUCCESS_MARKET_STATUS_RESULT,
} from "@/generated/TradingGateway"
import { withConnection } from "@/services/withConnection"

import { clearEquitiesError, publishEquitiesError } from "../errorAlert"
import { MarketDataService, type MarketStatusResult } from "../TradingGateway"

export enum MarketStatus {
  Open = "open",
  Closed = "closed",
}

// Both failure modes resolve to Closed, so this alert is the only signal that
// separates a genuinely closed market from a broken one.
const publishMarketStatusError = () => {
  publishEquitiesError({
    source: "marketStatus",
    message: t("marketStatus.errors.serviceUnavailable"),
  })
}

/**
 * `catchError` sits above `withConnection` so a transport failure collapses to
 * `Closed` without terminating the outer stream — the next CONNECTED status
 * resubscribes `defer` and the gateway re-sends the current status.
 */
export const getMarketStatus$ = defer(() =>
  MarketDataService.subscribeToMarketStatus(),
).pipe(
  tap({
    next: (result) => {
      if (result.type === ERROR_MARKET_STATUS_RESULT) {
        // the raw failure stays in the console; users get generic translated copy
        console.error("[marketStatus] Server-returned error:", result.payload)
        publishMarketStatusError()
        return
      }
      clearEquitiesError("marketStatus")
    },
  }),
  map((result: MarketStatusResult): MarketStatus => {
    if (result.type !== SUCCESS_MARKET_STATUS_RESULT) {
      return MarketStatus.Closed
    }
    return result.payload.isMarketOpen ? MarketStatus.Open : MarketStatus.Closed
  }),
  catchError((err) => {
    console.error("[marketStatus] Hydra stream error:", err)
    publishMarketStatusError()
    return of(MarketStatus.Closed)
  }),
  withConnection(),
  distinctUntilChanged(),
)

// QuoteDataPoint has no Suspense boundary, so prices stay hidden until the
// gateway says otherwise.
export const [useMarketStatus] = bind<MarketStatus>(
  getMarketStatus$,
  MarketStatus.Closed,
)
